import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ThinTextField,
  RejectModal,
  ThinTextAreaField,
} from "../../../../common/GeneralComponent";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchData } from "../../../../../utils/functions";
import { useNavigate } from "react-router-dom";

const CompletedFields = ({ data, onDataUpdate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state);
  const userRole = loading?.auth?.user?.user_role_name || "";
  const isSuperAdmin = userRole === "Super Admin";
  const implementationDetails = data?.implementationDetails || {};
  const featureName = data?.feature_name || "Feature";


  const completionDate = implementationDetails?.date_of_completion || "";
  const infraStructureImage = data?.infraStructureImage || null;
  const certificateCompletionImage = data?.certificateCompletionImage || null;


  // Physical condition
  const physicalCondition = implementationDetails?.physical_condition || "";
  const physicalConditionDetails = implementationDetails?.details_of_issue || "";

  // O&M Status - attachments at root level
  const omStatus = implementationDetails?.status_of_om || "";
  const omPlanImage = data?.omPlanImage || null;
  const sessionAttendenceImage = data?.sessionAttendenceImage || null;
  const sessionImage = data?.sessionImage || null;
  const omPlannedDate = implementationDetails?.plan_date || null;
  const omRemarks = implementationDetails?.plan_remarks || null;


  // Handover Status - attachments at root level
  const handoverStatus = implementationDetails?.status_of_handing_over || "";
  const handOverCertificateImage = data?.handOverCertificateImage || null;
  const handoverPlannedDate = implementationDetails?.handing_over_planned_date || null;
  const handoverRemarks = implementationDetails?.handing_over_planned_remarks || null;


  // Verification Checklist
  const infrastructureInUse = implementationDetails?.infrastructure_in_use || "";
  const infrastructureInUseComments = implementationDetails?.infrastructure_in_use_comments || "";
  const visibleDamage = implementationDetails?.visible_damage || "";
  const visibleDamageComments = implementationDetails?.visible_damage_comments || "";
  const communityConsulted = implementationDetails?.community_consulted || "";
  const communityConsultedComments = implementationDetails?.community_consulted_comments || "";
  const overallRemarks = implementationDetails?.overall_remarks || "";

  const [rejectModal, setRejectModal] = useState({
    isOpen: false,
    reason: "",
  });

  // Image modal states
  const [showInfraModal, setShowInfraModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [showOmPlanModal, setShowOmPlanModal] = useState(false);
  const [showSessionAttendModal, setShowSessionAttendModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showHandoverModal, setShowHandoverModal] = useState(false);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    const cleanPath = imagePath.replace(/^undefined\//, "");
    if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
      return cleanPath;
    }
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    return `${baseUrl}/${cleanPath}`;
  };

  const formatDateTime = (d) => {
    if (!d) return "N/A";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (!(dt instanceof Date) || isNaN(dt.getTime())) {
      return String(d);
    }
    const day = String(dt.getDate()).padStart(2, "0");
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const year = dt.getFullYear();
    let hours = dt.getHours();
    const minutes = String(dt.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} at ${hours}:${minutes} ${ampm}`;
  };

  const handleApprove = async () => {
    try {
      const approvalType = userRole === "DQA" ? "dqa-approve" : userRole === "Project Manager" ? "pm-approve" : "";
      const endpoint = `implementation-phase-details/approve/${data.boqs_proposed_implementation_id}`;
      await fetchData(endpoint, "PUT", { type: approvalType }, {}, {}, navigate, null, dispatch);
      if (onDataUpdate) {
        onDataUpdate();
      }
      window.location.reload();
      toast.success(`Feature approved successfully by ${approvalType.toUpperCase()}`);
    } catch (error) {
      console.error("Error approving feature:", error);
      toast.error("Failed to approve feature");
    }
  };

  const handleOverallReject = () => {
    setRejectModal({ isOpen: true, reason: "" });
  };

  const submitRejection = async (e) => {
    e.preventDefault();
    if (!rejectModal.reason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    try {
      const rejectionType = userRole === "DQA" ? "dqa-approve" : userRole === "Project Manager" ? "pm-approve" : "";
      const endpoint = `implementation-phase-details/reject/${data.boqs_proposed_implementation_id}`;
      await fetchData(endpoint, "PUT", { type: rejectionType, remarks: rejectModal.reason }, {}, {}, navigate, null, dispatch);
      toast.success(`Feature rejected successfully by ${rejectionType.toUpperCase()}`);
      setRejectModal({ isOpen: false, reason: "" });
      if (onDataUpdate) {
        onDataUpdate();
      }
      window.location.reload();
    } catch (error) {
      console.error("Error rejecting feature:", error);
      toast.error("Failed to reject feature");
    }
  };

  const closeModal = () => {
    setRejectModal({ isOpen: false, reason: "" });
  };

  // Image modal handlers
  const handleInfraClick = (e) => { e.preventDefault(); e.stopPropagation(); if (infraStructureImage) setShowInfraModal(true); };
  const closeInfraModal = (e) => { e?.stopPropagation(); setShowInfraModal(false); };
  const handleCertClick = (e) => { e.preventDefault(); e.stopPropagation(); if (certificateCompletionImage) setShowCertModal(true); };
  const closeCertModal = (e) => { e?.stopPropagation(); setShowCertModal(false); };
  const handleOmPlanClick = (e) => { e.preventDefault(); e.stopPropagation(); if (omPlanImage) setShowOmPlanModal(true); };
  const closeOmPlanModal = (e) => { e?.stopPropagation(); setShowOmPlanModal(false); };
  const handleSessionAttendClick = (e) => { e.preventDefault(); e.stopPropagation(); if (sessionAttendenceImage) setShowSessionAttendModal(true); };
  const closeSessionAttendModal = (e) => { e?.stopPropagation(); setShowSessionAttendModal(false); };
  const handleSessionClick = (e) => { e.preventDefault(); e.stopPropagation(); if (sessionImage) setShowSessionModal(true); };
  const closeSessionModal = (e) => { e?.stopPropagation(); setShowSessionModal(false); };
  const handleHandoverClick = (e) => { e.preventDefault(); e.stopPropagation(); if (handOverCertificateImage) setShowHandoverModal(true); };
  const closeHandoverModal = (e) => { e?.stopPropagation(); setShowHandoverModal(false); };

  const isDQAApproved = implementationDetails?.verified_from_dqa;
  const dqaApprovedAt = implementationDetails?.verified_from_dqa_datetime;
  const isPMApproved = implementationDetails?.verified_from_pm;
  const pmApprovedAt = implementationDetails?.verified_from_pm_datetime;
  const rejection_remarks = implementationDetails?.rejection_remarks || null;

  return (
    <div className="w-full bg-white rounded">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">{featureName} — Completed Implementation</h2>
      </div>
      <div className="w-full">
        {/* Date of Completion and Photos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField
              isRequired={false}
              label="Date of Completion"
              value={completionDate ? new Date(completionDate).toLocaleDateString() : "N/A"}
              readOnly
            />
          </div>
          {infraStructureImage && (
            <div className="w-full">
              <label className="text-sm font-[Regular] text-[#565656] mb-1 block">Infrastructure Photo</label>
              <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                <button onClick={handleInfraClick} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0">
                  <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                  <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">Infrastructure Photo</span>
                  <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                </button>
              </div>
            </div>
          )}
          {certificateCompletionImage && (
            <div className="w-full">
              <label className="text-sm font-[Regular] text-[#565656] mb-1 block">Completion Certificate</label>
              <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                <button onClick={handleCertClick} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0">
                  <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                  <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">Completion Certificate</span>
                  <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                </button>
              </div>
            </div>
          )}
          {!infraStructureImage && !certificateCompletionImage && <div className="w-full"></div>}
        </div>

        {/* Physical Condition */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField isRequired={false} label="Physical Condition" value={physicalCondition} readOnly />
          </div>
          {physicalCondition !== "Satisfactory" && physicalConditionDetails ? (
            <>
              <div className="w-full md:col-span-2">
                <ThinTextAreaField isRequired={false} label="Details of Issue / Action Required" value={physicalConditionDetails} readOnly />
              </div>
              <div className="w-full"></div> {/* Balance to 3 columns */}
            </>
          ) : (
            <>
              <div className="w-full"></div>
              <div className="w-full"></div>
            </>
          )}
        </div>

        {/* O&M Plan */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField isRequired={false} label="Status of O&M Plan and Orientation" value={omStatus} readOnly />
          </div>
          {omStatus === "Completed" || omStatus === "Completed / Developed" ? (
            <>
              {omPlanImage && (
                <div className="w-full">
                  <label className="text-sm font-[Regular] text-[#565656] mb-1 block">O&M Plan</label>
                  <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                    <button onClick={handleOmPlanClick} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0">
                      <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                      <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">O&M Plan</span>
                      <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                    </button>
                  </div>
                </div>
              )}
              {sessionAttendenceImage && (
                <div className="w-full">
                  <label className="text-sm font-[Regular] text-[#565656]  mb-1 block">Attendance Sheet</label>
                  <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                    <button onClick={handleSessionAttendClick} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0">
                      <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                      <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">Attendance Sheet</span>
                      <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-full">
                <ThinTextField
                  isRequired={false}
                  label="Planned Date"
                  value={omPlannedDate ? new Date(omPlannedDate).toLocaleDateString() : "Not provided"}
                  readOnly
                />
              </div>
              <div className="w-full">
                <ThinTextAreaField isRequired={false} label="Remarks" value={omRemarks || "No remarks provided"} readOnly />
              </div>
            </>
          )}
        </div>

        {/* Orientation Photo - separate row if exists */}
        {sessionImage && (omStatus === "Completed" || omStatus === "Completed / Developed") && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="w-full">
              <label className="text-sm font-[Regular] text-[#565656] mb-1 block">Orientation Photo</label>
              <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                <button onClick={handleSessionClick} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0">
                  <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                  <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">Orientation Photo</span>
                  <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                </button>
              </div>
            </div>
            <div className="w-full"></div>
            <div className="w-full"></div>
          </div>
        )}

        {/* Handing Over */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField isRequired={false} label="Status of Handing Over" value={handoverStatus} readOnly />
          </div>
          {handoverStatus === "Completed" ? (
            handOverCertificateImage && (
              <div className="w-full">
                <label className="text-sm font-[Regular] text-[#565656] mb-1 block">Handover Certificate</label>
                <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                  <button onClick={handleHandoverClick} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0">
                    <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                    <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">Handover Certificate</span>
                    <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                  </button>
                </div>
              </div>
            )
          ) : (
            <>
              <div className="w-full">
                <ThinTextField
                  isRequired={false}
                  label="Planned Date"
                  value={handoverPlannedDate ? new Date(handoverPlannedDate).toLocaleDateString() : "Not provided"}
                  readOnly
                />
              </div>
              <div className="w-full">
                <ThinTextAreaField isRequired={false} label="Remarks" value={handoverRemarks || "No remarks provided"} readOnly />
              </div>
            </>
          )}
          {handoverStatus !== "Completed" && !handOverCertificateImage && <div className="w-full"></div>}
        </div>

        {/* Verification Checklist */}
        {/* <div className="mb-4">
          <h3 className="text-md font-semibold mb-3">Verification Checklist</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="w-full">
              <ThinTextField isRequired={false} label="Is the Infrastructure in Use?" value={infrastructureInUse} readOnly />
            </div>
            <div className="w-full">
              <ThinTextField isRequired={false} label="Is There Visible Damage or Defect?" value={visibleDamage} readOnly />
            </div>
            <div className="w-full">
              <ThinTextField isRequired={false} label="Was the Community Consulted?" value={communityConsulted} readOnly />
            </div>
          </div> */}
          {/* Comments if any "No" or "Yes" for damage */}
          {/* {(infrastructureInUse === "No" || visibleDamage === "Yes" || communityConsulted === "No") && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {infrastructureInUse === "No" && infrastructureInUseComments && (
                <div className="w-full">
                  <ThinTextField isRequired={false} label="Infrastructure Use Comments" value={infrastructureInUseComments} readOnly />
                </div>
              )}
              {visibleDamage === "Yes" && visibleDamageComments && (
                <div className="w-full">
                  <ThinTextField isRequired={false} label="Damage Comments" value={visibleDamageComments} readOnly />
                </div>
              )}
              {communityConsulted === "No" && communityConsultedComments && (
                <div className="w-full">
                  <ThinTextField isRequired={false} label="Community Consultation Comments" value={communityConsultedComments} readOnly />
                </div>
              )}
              {/* Fill empty slots to balance 3 columns */}
              {/* {!(infrastructureInUse === "No" && infrastructureInUseComments) && <div className="w-full"></div>}
              {!(visibleDamage === "Yes" && visibleDamageComments) && <div className="w-full"></div>}
              {!(communityConsulted === "No" && communityConsultedComments) && <div className="w-full"></div>}
            </div>
          )} */}
          {/* Overall Remarks */}
          {/* {overallRemarks && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="w-full md:col-span-3">
                <ThinTextAreaField isRequired={false} label="Overall Remarks / Observations" value={overallRemarks} readOnly />
              </div>
            </div>
          )}
        </div> */}
      </div>

      {/* Approval Section */}
      {/* {!isSuperAdmin && (
        <>
          {userRole === "DQA" && isDQAApproved === null && (
            <div className="flex justify-end gap-3 pt-3 border-t">
              <button type="button" onClick={handleApprove} className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors text-sm">Approve</button>
              <button type="button" onClick={handleOverallReject} className="bg-red-600 text-white rounded-lg px-4 py-2 hover:bg-red-700 transition-colors text-sm">Reject</button>
            </div>
          )}
          {userRole === "Project Manager" && isDQAApproved === 1 && isPMApproved === null && (
            <div className="flex justify-end gap-3 pt-3 border-t">
              <button type="button" onClick={handleApprove} className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors text-sm">Approve</button>
              <button type="button" onClick={handleOverallReject} className="bg-red-600 text-white rounded-lg px-4 py-2 hover:bg-red-700 transition-colors text-sm">Reject</button>
            </div>
          )}
        </>
      )} */}

      {/* Status Display */}
      {/* {(isDQAApproved !== null || isPMApproved !== null) && (
        <div className="flex flex-col justify-end items-end gap-2 pt-3 border-t">
          {(() => {
            let finalStatus = null;
            let decidedBy = "";
            let decidedAt = "";
            let reason = "";
            if (isPMApproved === 1 || isPMApproved === 0) {
              finalStatus = isPMApproved === 1 ? "Approved" : "Rejected";
              decidedBy = "PM";
              decidedAt = pmApprovedAt;
              reason = isPMApproved === 0 ? rejection_remarks : "";
            } else if (isDQAApproved === 1 || isDQAApproved === 0) {
              finalStatus = isDQAApproved === 1 ? "Approved" : "Rejected";
              decidedBy = "DQA";
              decidedAt = dqaApprovedAt;
              reason = isDQAApproved === 0 ? rejection_remarks : "";
            } else {
              return null;
            }
            if (isSuperAdmin) {
              if (isPMApproved === 0) {
                finalStatus = "Rejected";
                decidedBy = "PM";
                decidedAt = pmApprovedAt;
                reason = rejection_remarks;
              } else if (isPMApproved === null && isDQAApproved === 1) {
                finalStatus = "Approved";
                decidedBy = "DQA";
                decidedAt = dqaApprovedAt;
              }
            }
            return (
              <>
                <span className="text-sm font-semibold text-gray-500 italic">
                  This section has been{" "}
                  <span className={finalStatus === "Approved" ? "text-green-600" : "text-red-600"}>{finalStatus}</span>{" "}
                  by {decidedBy} on {formatDateTime(decidedAt)}.
                </span>
                {finalStatus === "Rejected" && reason && (
                  <span className="text-sm font-semibold text-gray-500 italic text-red-600">REASON: {reason}</span>
                )}
              </>
            );
          })()}
        </div>
      )} */}

      {/* Reject Modal */}
      {/* {rejectModal.isOpen && (
        <RejectModal
          isOpen={rejectModal.isOpen}
          reason={rejectModal.reason}
          onClose={closeModal}
          onChangeReason={(val) => setRejectModal((prev) => ({ ...prev, reason: val }))}
          onSubmit={submitRejection}
        />
      )} */}

      {/* Image Modals */}
      {/* {showInfraModal && infraStructureImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]" onClick={closeInfraModal}>
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeInfraModal} className="absolute top-2 right-2 text-gray-600 hover:text-black">✕</button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">Infrastructure Photo</h2>
            <img src={getImageUrl(infraStructureImage)} alt="Infrastructure Photo" className="max-h-[80vh] object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/600x400?text=Image+not+found"; }} />
            <a href={getImageUrl(infraStructureImage)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center">View Full Image</a>
          </div>
        </div>
      )} */}
      {/* {showCertModal && certificateCompletionImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]" onClick={closeCertModal}>
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeCertModal} className="absolute top-2 right-2 text-gray-600 hover:text-black">✕</button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">Completion Certificate</h2>
            <img src={getImageUrl(certificateCompletionImage)} alt="Completion Certificate" className="max-h-[80vh] object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/600x400?text=Image+not+found"; }} />
            <a href={getImageUrl(certificateCompletionImage)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center">View Full Image</a>
          </div>
        </div>
      )} */}
      {/* {showOmPlanModal && omPlanImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]" onClick={closeOmPlanModal}>
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeOmPlanModal} className="absolute top-2 right-2 text-gray-600 hover:text-black">✕</button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">O&M Plan</h2>
            <img src={getImageUrl(omPlanImage)} alt="O&M Plan" className="max-h-[80vh] object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/600x400?text=Image+not+found"; }} />
            <a href={getImageUrl(omPlanImage)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center">View Full Image</a>
          </div>
        </div>
      )}
      {showSessionAttendModal && sessionAttendenceImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]" onClick={closeSessionAttendModal}>
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeSessionAttendModal} className="absolute top-2 right-2 text-gray-600 hover:text-black">✕</button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">Attendance Sheet</h2>
            <img src={getImageUrl(sessionAttendenceImage)} alt="Attendance Sheet" className="max-h-[80vh] object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/600x400?text=Image+not+found"; }} />
            <a href={getImageUrl(sessionAttendenceImage)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center">View Full Image</a>
          </div>
        </div>
      )} */}
      {/* {showSessionModal && sessionImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]" onClick={closeSessionModal}>
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeSessionModal} className="absolute top-2 right-2 text-gray-600 hover:text-black">✕</button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">Orientation Photo</h2>
            <img src={getImageUrl(sessionImage)} alt="Orientation Photo" className="max-h-[80vh] object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/600x400?text=Image+not+found"; }} />
            <a href={getImageUrl(sessionImage)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center">View Full Image</a>
          </div>
        </div>
      )}
      {showHandoverModal && handOverCertificateImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]" onClick={closeHandoverModal}>
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeHandoverModal} className="absolute top-2 right-2 text-gray-600 hover:text-black">✕</button>
            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">Handover Certificate</h2>
            <img src={getImageUrl(handOverCertificateImage)} alt="Handover Certificate" className="max-h-[80vh] object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/600x400?text=Image+not+found"; }} />
            <a href={getImageUrl(handOverCertificateImage)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center">View Full Image</a>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default CompletedFields;